import React, { useEffect, useState, useCallback } from 'react';
import { Clock, Info } from 'lucide-react';
import FeatureGate from '../../components/FeatureGate';
import { useToast } from '../../components/ui/Toast';
import './OrderExpirySection.css';

/**
 * Sales Tools — Phase 3.4.
 *
 * Inline Settings card that controls the per-store auto-cancel
 * policy for unpaid orders. The configuration body is now tabbed
 * between Global (one window for every channel) and Per-channel
 * (PRO+, individual windows per channel) — the merchant focuses on
 * one concept at a time instead of seeing both at once.
 */

// POS intentionally excluded — POS orders are pre-billed for service
// charge at creation and are managed face-to-face by the cashier.
// Auto-cancelling them would conflict with platform accounting.
const CHANNELS = [
  { key: 'STOREFRONT', label: 'Storefront', hint: 'Cart abandonment, usually short' },
  { key: 'WHATSAPP',   label: 'WhatsApp',   hint: 'Chat negotiation, longer' },
  { key: 'MANUAL',     label: 'Manual',     hint: 'Recorded sale on terms, longest' },
];

const MIN_HOURS = 1;
const MAX_HOURS = 8760;

function clampHours(value, fallback) {
  const n = parseInt(value, 10);
  if (Number.isFinite(n)) return Math.min(Math.max(n, MIN_HOURS), MAX_HOURS);
  return fallback;
}

/* ─── Module-scope sub-components ─────────────────────────────── */

function HoursInput({ value, onChange, ariaLabel, size = 'md' }) {
  return (
    <input
      className={`sl-oe-input sl-oe-input--${size}`}
      type="number"
      min={MIN_HOURS}
      max={MAX_HOURS}
      step={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
    />
  );
}

function TabSwitcher({ value, onChange, perChannelLocked }) {
  return (
    <div className="sl-oe-tabs" role="tablist" aria-label="Expiry policy mode">
      <button
        type="button"
        role="tab"
        aria-selected={value === 'global'}
        className={`sl-oe-tab ${value === 'global' ? 'is-active' : ''}`}
        onClick={() => onChange('global')}
      >
        Global rule
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === 'per-channel'}
        className={`sl-oe-tab ${value === 'per-channel' ? 'is-active' : ''}`}
        onClick={() => onChange('per-channel')}
      >
        <span>Per channel</span>
        {perChannelLocked && <span className="sl-oe-tab-badge">PRO</span>}
      </button>
    </div>
  );
}

function GlobalPanel({ hours, onHoursChange }) {
  return (
    <div className="sl-oe-panel">
      <div className="sl-oe-panel-head">
        <p className="sl-oe-panel-title">One rule for every channel</p>
        <p className="sl-oe-panel-sub">
          Same window applies to storefront, POS, WhatsApp, and manual orders.
        </p>
      </div>
      <div className="sl-oe-panel-control">
        <label className="sl-oe-inline-label" htmlFor="sl-oe-global-hours">
          Cancel after
        </label>
        <HoursInput
          value={hours}
          onChange={onHoursChange}
          ariaLabel="Global expiry hours"
          size="lg"
        />
        <span className="sl-oe-unit">hours</span>
      </div>
      <p className="sl-oe-helper">
        <Info size={12} aria-hidden />
        <span>
          The earliest practical window is 1 hour; the longest is {MAX_HOURS / 24} days.
        </span>
      </p>
    </div>
  );
}

function ChannelOverrideRow({ channel, hoursOrGlobal, onChange, isOverride, onToggleOverride }) {
  return (
    <div className="sl-oe-channel-row">
      <div className="sl-oe-channel-info">
        <p className="sl-oe-channel-label">{channel.label}</p>
        <p className="sl-oe-channel-hint">{channel.hint}</p>
      </div>
      <div className="sl-oe-channel-control">
        <label className="sl-oe-override">
          <input
            type="checkbox"
            checked={isOverride}
            onChange={(e) => onToggleOverride(e.target.checked)}
          />
          <span>Override</span>
        </label>
        <HoursInput
          value={hoursOrGlobal}
          onChange={onChange}
          ariaLabel={`${channel.label} expiry hours`}
        />
        <span className="sl-oe-unit">hr</span>
      </div>
    </div>
  );
}

function PerChannelPanel({ perChannelDraft, globalHours, setOverride, setHours }) {
  return (
    <div className="sl-oe-panel">
      <div className="sl-oe-panel-head">
        <p className="sl-oe-panel-title">Different windows per channel</p>
        <p className="sl-oe-panel-sub">
          A 4-hour POS no-show doesn’t need the same window as a 7-day WhatsApp negotiation.
          Toggle Override on any channel to set its own window; unchecked channels fall back
          to the global rule.
        </p>
      </div>
      <div className="sl-oe-channels">
        {CHANNELS.map((ch) => {
          const isOverride = perChannelDraft != null && perChannelDraft[ch.key] != null;
          return (
            <ChannelOverrideRow
              key={ch.key}
              channel={ch}
              isOverride={isOverride}
              hoursOrGlobal={isOverride ? perChannelDraft[ch.key] : globalHours}
              onChange={(v) => setHours(ch.key, v)}
              onToggleOverride={(checked) => setOverride(ch.key, checked)}
            />
          );
        })}
      </div>
    </div>
  );
}

function PerChannelLocked() {
  return (
    <div className="sl-oe-panel sl-oe-panel--locked">
      <div className="sl-oe-panel-head">
        <p className="sl-oe-panel-title">Per-channel expiry is a PRO feature</p>
        <p className="sl-oe-panel-sub">
          Upgrade to set different auto-cancel windows for storefront, POS, WhatsApp, and
          manual orders independently.
        </p>
      </div>
      <a href="/upgrade" className="sl-oe-upgrade-cta">View plans</a>
    </div>
  );
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function OrderExpirySection() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [globalHours, setGlobalHours] = useState(72);
  const [perChannelDraft, setPerChannelDraft] = useState(null);
  const [tab, setTab] = useState('global');
  const [lastSweep, setLastSweep] = useState(null); // { at: ISO, summary?: {...} }
  // Track the original enabled value so we can ask for confirmation
  // when the merchant flips it OFF (the cancellations the cron has
  // already made won't be reactivated).
  const [originalEnabled, setOriginalEnabled] = useState(false);

  const fetchPolicy = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch('/api/merchant/store-expiry-policy', {
        credentials: 'include',
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.policy) {
        const isEnabled = !!data.policy.enabled;
        setEnabled(isEnabled);
        setOriginalEnabled(isEnabled);
        setGlobalHours(Number(data.policy?.global?.hours) || 72);
        const pc = data.policy.perChannel;
        if (pc && typeof pc === 'object' && Object.keys(pc).length > 0) {
          const flat = {};
          for (const ch of CHANNELS) {
            if (pc[ch.key]?.hours != null) flat[ch.key] = Number(pc[ch.key].hours);
          }
          setPerChannelDraft(Object.keys(flat).length > 0 ? flat : null);
          // If the merchant already has per-channel config, default the
          // tab to per-channel so they land on the configuration they
          // actually use.
          setTab(Object.keys(flat).length > 0 ? 'per-channel' : 'global');
        } else {
          setPerChannelDraft(null);
        }
        setLastSweep(data.lastSweep || null);
      }
    } catch {
      toast.error('Could not load order expiry policy');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const setChannelOverride = useCallback(
    (channelKey, isOverride) => {
      setPerChannelDraft((prev) => {
        const next = { ...(prev || {}) };
        if (isOverride) {
          if (next[channelKey] == null) next[channelKey] = globalHours;
        } else {
          delete next[channelKey];
        }
        return Object.keys(next).length === 0 ? null : next;
      });
    },
    [globalHours]
  );

  const setChannelHours = useCallback((channelKey, value) => {
    setPerChannelDraft((prev) => {
      const next = { ...(prev || {}) };
      next[channelKey] = clampHours(value, 72);
      return next;
    });
  }, []);

  // Format the "Last run: 10m ago" hint. In-memory backend cache so
  // null on fresh process boot — we hide the hint entirely in that case.
  const lastRunLabel = (() => {
    if (!lastSweep?.at) return null;
    const ms = Date.now() - new Date(lastSweep.at).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'Last run: just now';
    if (m < 60) return `Last run: ${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `Last run: ${h}h ago`;
    return `Last run: ${Math.floor(h / 24)}d ago`;
  })();

  const handleSave = useCallback(async () => {
    // When flipping the toggle OFF, surface a quick confirm — past
    // auto-cancellations don't reactivate, so set expectations.
    if (originalEnabled && !enabled) {
      const proceed = window.confirm(
        'Turning auto-cancel off stops the cron from cancelling new unpaid orders going forward. ' +
        'Previously cancelled orders stay cancelled — they will not be reactivated. Continue?'
      );
      if (!proceed) return;
    }
    setSaving(true);
    const payload = {
      enabled,
      global: { hours: clampHours(globalHours, 72) },
    };
    if (perChannelDraft && Object.keys(perChannelDraft).length > 0) {
      payload.perChannel = {};
      for (const [ch, hours] of Object.entries(perChannelDraft)) {
        payload.perChannel[ch] = { hours: clampHours(hours, 72) };
      }
    }
    try {
      const resp = await fetch('/api/merchant/store-expiry-policy', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(data.error || 'Could not save order expiry policy');
      } else {
        toast.success('Order rules saved');
        setOriginalEnabled(enabled);
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  }, [enabled, globalHours, perChannelDraft, originalEnabled, toast]);

  if (loading) {
    return (
      <section className="st-card" id="order-rules">
        <div className="st-card-body">
          <div className="sl-oe-skeleton" />
        </div>
      </section>
    );
  }

  return (
    <section className="st-card sl-oe-card" id="order-rules">
      <div className="st-card-body">
        <div className="st-card-head">
          <h2 className="st-card-title">
            <Clock size={16} aria-hidden /> Order rules
          </h2>
          <p className="st-card-sub">
            Stale unpaid orders pile up in Receivables and inflate “outstanding” figures.
            Auto-cancel them so your reports stay clean.
          </p>
        </div>

        <label className="sl-oe-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span className="sl-oe-toggle-label">Auto-cancel unpaid orders</span>
        </label>

        {enabled && (
          <div className="sl-oe-body">
            <TabSwitcher
              value={tab}
              onChange={setTab}
              perChannelLocked={false /* gated below; PRO badge is informational */}
            />

            {tab === 'global' && (
              <GlobalPanel
                hours={globalHours}
                onHoursChange={(v) => setGlobalHours(clampHours(v, 72))}
              />
            )}

            {tab === 'per-channel' && (
              <FeatureGate featureId="sales.expiry_per_channel" fallback={<PerChannelLocked />}>
                <PerChannelPanel
                  perChannelDraft={perChannelDraft}
                  globalHours={globalHours}
                  setOverride={setChannelOverride}
                  setHours={setChannelHours}
                />
              </FeatureGate>
            )}
          </div>
        )}

        <div className="st-card-foot sl-oe-foot">
          {lastRunLabel && <span className="sl-oe-lastrun">{lastRunLabel}</span>}
          <button
            type="button"
            className="st-btn st-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </section>
  );
}
